// ===============================================================
// 🔐 useLogin.js — نظام تسجيل دخول احترافي (Server-first)
// - After ANY auth success -> ensureUserProfile() (Cloud Function)
// - Then keep your existing navigation logic via checkUserInFirestore
// path: src/hooks/useLogin.js
// ===============================================================

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  signInWithPopup,
  signInWithPhoneNumber,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  EmailAuthProvider,
  linkWithCredential,
} from "firebase/auth";

import {
  auth,
  appleProvider,
  functions,
  httpsCallable,
} from "../firebase";
import {
  getOrCreateRecaptcha,
  resetRecaptcha,
} from "../utils/recaptcha";
import { checkUserInFirestore } from "../services/checkUserInFirestore";
import {
  signInWithGoogle,
  getGoogleSignInErrorMessage,
  logGoogleSignInError,
} from "../utils/googleSignIn";

export const useLogin = (navigate) => {
  const { t } = useTranslation();
  const [isSocialLoginLoading, setIsSocialLoginLoading] = useState(false);
  const [isPhoneSendingCode, setIsPhoneSendingCode] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);

  // ===============================================================
  // ✅ Server-first: ensureUserProfile (creates/merges user doc safely)
  // ===============================================================
  const ensureProfile = async (firebaseUser) => {
    try {
      if (!firebaseUser) return;

      // refresh token to ensure callable sees latest auth
      try {
        await firebaseUser.getIdToken(true);
      } catch (_) {}

      const fn = httpsCallable(functions, "ensureUserProfile");
      await fn({}); // server uses context.auth.uid
    } catch (e) {
      console.error("ensureUserProfile error:", e);
      // لا نوقف تسجيل الدخول بسببها
    }
  };

  // ===============================================================
  // ✅ Finalize login flow (server -> firestore nav)
  // ===============================================================
  const finalizeLogin = async (firebaseUser) => {
    await ensureProfile(firebaseUser);
    await checkUserInFirestore(firebaseUser, navigate);
  };

  // =========================
  // ⭐ GOOGLE / APPLE LOGIN
  // =========================
  const handleSocialLogin = async (providerName) => {
    if (isSocialLoginLoading || isPhoneSendingCode || isEmailLoading) return;

    try {
      setIsSocialLoginLoading(true);

      if (providerName === "google") {
        const result = await signInWithGoogle();
        await finalizeLogin(result.user);
        return;
      }

      const result = await signInWithPopup(auth, appleProvider);
      await finalizeLogin(result.user);
    } catch (e) {
      if (e?.code === "auth/cancelled") return;

      if (providerName === "google") {
        logGoogleSignInError(e);
        alert(getGoogleSignInErrorMessage(e, t));
        return;
      }

      console.error("Social login error:", e);

      alert(t("auth.social_login_failed", { defaultValue: "Could not sign in with this provider." }));
    } finally {
      setIsSocialLoginLoading(false);
    }
  };

  // =========================
  // 📱 PHONE LOGIN
  // =========================
  const handlePhoneLogin = async (phone) => {
    if (!phone || !phone.startsWith("+")) {
      alert(t("auth.phone_format_error", { defaultValue: "Please enter a phone number in a format like +43..." }));
      return;
    }

    const phoneSafe = phone.trim();

    try {
      setIsPhoneSendingCode(true);

      const appVerifier = await getOrCreateRecaptcha();

      const confirmation = await signInWithPhoneNumber(
        auth,
        phoneSafe,
        appVerifier
      );

      const code = prompt(t("auth.enter_verification_code", { defaultValue: "Enter the verification code:" }));
      if (!code) return;

      const result = await confirmation.confirm(code.trim());
      await finalizeLogin(result.user);
    } catch (e) {
      console.error("Phone login error:", {
        code: e?.code,
        message: e?.message || e,
        customData: e?.customData,
        phone: phoneSafe,
      });
      if (
        e?.code === "auth/invalid-app-credential" ||
        e?.code === "auth/captcha-check-failed"
      ) {
        resetRecaptcha();
      }
      alert(t("auth.phone_login_failed", { defaultValue: "Phone sign-in failed." }));
    } finally {
      setIsPhoneSendingCode(false);
    }
  };

  // =========================
  // ✉ EMAIL LOGIN (FULL LOGIC)
  // =========================
  const handleEmailLogin = async (email, password) => {
    if (!email || !password) {
      alert(t("auth.email_password_required", { defaultValue: "Enter your email and password." }));
      return;
    }

    const emailSafe = email.trim().toLowerCase();
    const passwordSafe = password;

    try {
      setIsEmailLoading(true);

      // 1) حاول تسجيل الدخول أولاً
      try {
        const login = await signInWithEmailAndPassword(
          auth,
          emailSafe,
          passwordSafe
        );

        console.log("🔑 تسجيل دخول ناجح");
        await finalizeLogin(login.user);
        return;
      } catch (err) {
        const canTryCreate =
          err.code === "auth/user-not-found" ||
          err.code === "auth/invalid-credential" ||
          err.code === "auth/invalid-login-credentials";

        if (!canTryCreate) {
          throw err;
        }
      }

      // 2) إذا فشل الدخول، افحص هل هذا الإيميل عنده مزودات تسجيل
      const methods = await fetchSignInMethodsForEmail(auth, emailSafe);

      // إذا لا توجد أي طريقة تسجيل، فهذا يعني حساب غير موجود -> أنشئه
      if (!methods || methods.length === 0) {
        const newUser = await createUserWithEmailAndPassword(
          auth,
          emailSafe,
          passwordSafe
        );

        console.log("✨ حساب جديد تم إنشاؤه");
        await finalizeLogin(newUser.user);
        return;
      }

      // 3) إذا الإيميل موجود مع Google فقط -> اسمح بربط كلمة سر بعد تسجيل Google
      if (methods.includes("google.com") && !methods.includes("password")) {
        const googleResult = await signInWithGoogle();

        const sameEmail =
          googleResult?.user?.email &&
          googleResult.user.email.toLowerCase() === emailSafe;

        if (!sameEmail) {
          alert(t("auth.google_same_email_required", { defaultValue: "Please sign in with the same email linked to your Google account." }));
          return;
        }

        const credential = EmailAuthProvider.credential(
          emailSafe,
          passwordSafe
        );

        await linkWithCredential(googleResult.user, credential);

        console.log("🔗 تم ربط كلمة مرور مع حساب Google");
        await finalizeLogin(googleResult.user);
        return;
      }

      // 4) إذا الحساب موجود أصلًا بكلمة مرور أو بمزود آخر
      if (methods.includes("password")) {
        alert(t("auth.account_exists_wrong_password", { defaultValue: "This account already exists, but the password is incorrect." }));
        return;
      }

      alert(t("auth.email_linked_other_method", { defaultValue: "This email is linked to another sign-in method." }));
    } catch (error) {
      console.error(
        "❌ Email login error:",
        error?.code,
        error?.message,
        error
      );

      if (error.code === "auth/wrong-password") {
        alert(t("auth.wrong_password", { defaultValue: "Incorrect password." }));
      } else if (error.code === "auth/invalid-email") {
        alert(t("auth.invalid_email", { defaultValue: "Invalid email format." }));
      } else if (error.code === "auth/weak-password") {
        alert(t("auth.weak_password", { defaultValue: "Password must be at least 6 characters." }));
      } else if (error.code === "auth/email-already-in-use") {
        alert(t("auth.email_already_in_use", { defaultValue: "This email is already in use." }));
      } else if (error.code === "auth/account-exists-with-different-credential") {
        alert(t("auth.email_linked_different_credential", { defaultValue: "This email is linked to a different sign-in method." }));
      } else if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/cancelled"
      ) {
        return;
      } else if (
        error.code === "auth/missing-id-token" ||
        error.code === "auth/network-request-failed" ||
        error.code === "auth/google-sign-in-failed"
      ) {
        alert(getGoogleSignInErrorMessage(error, t));
      } else if (error.code === "auth/too-many-requests") {
        alert(t("auth.too_many_requests", { defaultValue: "Too many attempts. Please try again later." }));
      } else {
        alert(t("auth.email_login_failed", { defaultValue: "Email sign-in failed." }));
      }
    } finally {
      setIsEmailLoading(false);
    }
  };

  return {
    isSocialLoginLoading,
    isPhoneSendingCode,
    isEmailLoading,
    handleSocialLogin,
    handlePhoneLogin,
    handleEmailLogin,
  };
};
